import ClassNames from "Helpers/Common";
import { useState } from "react";
import { useAttributionWaterfall } from "CustomHooks/useConversions";

// Where paid conversions lose their ad click id.
//
// Deliberately starts from PAYING PATIENTS rather than from postbacks. A
// postback count only ever shows survivors, so it can look healthy while most
// revenue is unattributable; anchoring on the people who actually paid makes
// every step below it a measured loss instead of an inference.

const PCT = (n, d) => (d ? ((n / d) * 100).toFixed(1) : "0.0");

const Row = ({ label, n, total, indent = 0, tone = "", note }) => (
  <div className="flex items-baseline gap-3 py-1" style={{ paddingLeft: `${indent * 18}px` }}>
    <span className={`text-sm flex-1 ${tone}`}>{label}</span>
    {note && <span className="text-xs bali_hai">{note}</span>}
    <span className="text-sm font-medium text-black w-14 text-right">{n ?? 0}</span>
    <span className="text-xs bali_hai w-14 text-right">{PCT(n ?? 0, total)}%</span>
  </div>
);

export const AttributionPanel = ({ enabled }) => {
  const [days, setDays] = useState(14);
  const { data, isLoading, isFetching, isError } = useAttributionWaterfall({ days, enabled });

  if (!enabled) return null;
  if (isError) return <div className="mt-4 text-sm text-red-600">Could not load the attribution waterfall.</div>;
  if (isLoading || !data) return <div className="mt-4 text-sm bali_hai">Loading attribution waterfall…</div>;

  const t = data.totals || {};
  const total = t.payingPatients || 0;

  // Named explicitly rather than left to the reader: the biggest single drop
  // is the whole point of the panel.
  const steps = [
    { label: "paying → no PreRegister row (join failed)", lost: (t.payingPatients || 0) - (t.withPreReg || 0) },
    { label: "has PreRegister → no touch captured", lost: (t.withPreReg || 0) - (t.withTouch || 0) },
    { label: "has touch → no ad click id", lost: (t.withTouch || 0) - (t.withClickId || 0) },
    { label: "has click id → not posted to Google", lost: (t.withClickId || 0) - (t.postedGoogle || 0) },
  ].sort((a, b) => b.lost - a.lost);

  return (
    // Changing the day range refetches with keepPreviousData, so isLoading stays
    // false and the panel would sit on the old numbers with no sign that a
    // request is running. Dim while fetching, same as the tables.
    <div className={ClassNames("mt-4 flex flex-col gap-4", isFetching && "opacity-90 transition-opacity")}>
      <div className="rounded-md border border-gray-300 shadow-sm bg-white p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <span className="text-xs font-semibold text-black">Attribution waterfall — paid visits</span>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-8 px-2 rounded-md border border-gray-300 text-xs"
          >
            {[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>Last {d} days</option>)}
          </select>
        </div>

        <Row label="Paying patients" n={t.payingPatients} total={total} />
        <div className="text-xs bali_hai pb-2">
          revenue ${Number(t.revenue || 0).toFixed(2)} · attributable ${Number(t.revenueWithClickId || 0).toFixed(2)}
        </div>
        <Row label="…with a PreRegister row" n={t.withPreReg} total={total} indent={1} />
        <Row label="…with at least one touch" n={t.withTouch} total={total} indent={1} />
        <Row label="…with an ad CLICK ID" n={t.withClickId} total={total} indent={1} tone="font-medium" />
        <Row label="gclid" n={t.gclid} total={total} indent={2} />
        <Row label="gbraid, no gclid" n={t.gbraidOnly} total={total} indent={2} note="iOS" />
        <Row label="wbraid, no gclid" n={t.wbraidOnly} total={total} indent={2} note="web→app" />
        <Row label="fbclid" n={t.fbclid} total={total} indent={2} />
        <div className="border-t border-gray-200 my-2" />
        <Row label="Posted to Google" n={t.postedGoogle} total={total} />
        <Row label="Posted to Meta" n={t.postedMeta} total={total} />
      </div>

      <div className="rounded-md border border-gray-300 shadow-sm bg-white p-4">
        <div className="text-xs font-semibold text-black mb-2">Biggest drop-off</div>
        {steps.map((s) => (
          <div key={s.label} className="flex items-baseline gap-3 py-1">
            <span className="text-sm flex-1">{s.label}</span>
            <span className="text-sm font-medium text-black w-14 text-right">{s.lost}</span>
          </div>
        ))}
        <div className="border-t border-gray-200 my-3" />
        <div className="text-xs bali_hai flex flex-col gap-1">
          <div>
            <span className="font-medium text-black">{t.lastTouchLacksClick ?? 0}</span> paid rows whose LAST touch has no
            click id — these were silently dropped before the last-click fix.
          </div>
          <div>
            <span className={(t.appSourced ?? 0) > 0 ? "font-medium text-green-700" : "font-medium text-amber-700"}>
              {t.appSourced ?? 0}
            </span>{" "}
            paid rows with an APP-sourced touch (no landingPath). This is the Android install-referrer proof of life — zero
            means the referrer is not reaching the app.
          </div>
        </div>
      </div>

      <div className="rounded-md border border-gray-300 shadow-sm bg-white p-4">
        <div className="text-xs font-semibold text-black mb-2">Paying patients by first-touch channel</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs bali_hai text-left">
              <th className="py-1">Channel</th>
              <th className="py-1 text-right">Patients</th>
              <th className="py-1 text-right">With click id</th>
              <th className="py-1 text-right">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {(data.byChannel || []).map((c) => (
              <tr key={c.channel} className="border-t border-gray-100">
                <td className="py-1">{c.channel}</td>
                <td className="py-1 text-right">{c.patients}</td>
                <td className="py-1 text-right">{c.withClickId}</td>
                <td className="py-1 text-right">${Number(c.revenue || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
